/**
 * GeneratedImage — images the assistant can attach or publish on the user's behalf.
 *
 * Each row's bytes come from one of: a composer upload, a Google Drive download
 * (`display_drive_image`), or a browser screenshot (`browser_screenshot`). All
 * are stored in the assistant-uploads S3 bucket; the DynamoDB table
 * (`GeneratedImages`) holds metadata; the actual bytes live in S3.
 *
 * Storage layout (single DynamoDB table "GeneratedImages"):
 *   PK: imageId (S)  — globally unique image identifier (`img_<uuidv7>`)
 *   SK: sk    (S)  — always the constant GENERATED_IMAGE_SK ("META")
 *
 * GSIs:
 *   GeneratedImageByOwnerIndex       — PK=ownerUserId,     SK=createdAt (N)
 *   GeneratedImageByConversationIndex — PK=conversationId,  SK=createdAt (N)
 */

// ── Quality ───────────────────────────────────────────────────────────────────

export const GENERATED_IMAGE_QUALITY = {
  FAST: 'fast',
  HIGH: 'high',
} as const;

export type GeneratedImageQuality =
  (typeof GENERATED_IMAGE_QUALITY)[keyof typeof GENERATED_IMAGE_QUALITY];

// ── Aspect ratio ──────────────────────────────────────────────────────────────

export const GENERATED_IMAGE_ASPECT_RATIO = {
  SQUARE: '1:1',
  LANDSCAPE: '16:9',
  PORTRAIT: '9:16',
  FEED_PORTRAIT: '4:5',
} as const;

export type GeneratedImageAspectRatio =
  (typeof GENERATED_IMAGE_ASPECT_RATIO)[keyof typeof GENERATED_IMAGE_ASPECT_RATIO];

// ── Output format ─────────────────────────────────────────────────────────────

export const GENERATED_IMAGE_OUTPUT_FORMAT = {
  JPEG: 'jpeg',
  PNG: 'png',
} as const;

export type GeneratedImageOutputFormat =
  (typeof GENERATED_IMAGE_OUTPUT_FORMAT)[keyof typeof GENERATED_IMAGE_OUTPUT_FORMAT];

/** Format → MIME content-type. Shared by the S3 writer and the Instagram publish gateway. */
export const GENERATED_IMAGE_CONTENT_TYPE: Record<
  GeneratedImageOutputFormat,
  string
> = {
  [GENERATED_IMAGE_OUTPUT_FORMAT.JPEG]: 'image/jpeg',
  [GENERATED_IMAGE_OUTPUT_FORMAT.PNG]: 'image/png',
};

// ── Usage target ──────────────────────────────────────────────────────────────

export const GENERATED_IMAGE_USAGE_TARGET = {
  BRAIN_PAGE: 'BRAIN_PAGE',
  DOBOX_COMMENT: 'DOBOX_COMMENT',
} as const;

export type GeneratedImageUsageTarget =
  (typeof GENERATED_IMAGE_USAGE_TARGET)[keyof typeof GENERATED_IMAGE_USAGE_TARGET];

// ── Source ────────────────────────────────────────────────────────────────────

export const GENERATED_IMAGE_SOURCE = {
  AI_GENERATED: 'AI_GENERATED',
  USER_UPLOAD: 'USER_UPLOAD',
} as const;

export type GeneratedImageSource =
  (typeof GENERATED_IMAGE_SOURCE)[keyof typeof GENERATED_IMAGE_SOURCE];

// ── Model IDs ─────────────────────────────────────────────────────────────────

/**
 * Bedrock Stability model ids. No code path generates images with them anymore —
 * every `GeneratedImage` row's bytes come from a composer upload, a Drive
 * download, or a browser screenshot — but `storeImageBytes`/`registerUploadedImage`
 * (`apps/api/src/utils/images/storeImage.ts`) still stamp one of these onto every
 * row's `model` column, since the field is non-optional and historical rows
 * carry a real value here.
 */
export const GENERATED_IMAGE_MODEL_ID = {
  // Fast / cheaper Stability model. Same payload contract as Ultra.
  STABLE_IMAGE_CORE: 'stability.stable-image-core-v1:1',
  // High-quality Stability model (photorealism, multi-subject, fine detail).
  STABLE_IMAGE_ULTRA: 'stability.stable-image-ultra-v1:1',
} as const;

export type GeneratedImageModelId =
  (typeof GENERATED_IMAGE_MODEL_ID)[keyof typeof GENERATED_IMAGE_MODEL_ID];

// ── DynamoDB table / index / SK constants ─────────────────────────────────────

/** DynamoDB table name for generated image metadata. */
export const GENERATED_IMAGES_TABLE = 'GeneratedImages';

/** GSI name for listing images owned by a user newest-first (SK=createdAt). */
export const GENERATED_IMAGE_BY_OWNER_INDEX = 'GeneratedImageByOwnerIndex';

/**
 * GSI name for listing images belonging to a conversation newest-first (SK=createdAt).
 * Useful for future "images in this chat" views.
 */
export const GENERATED_IMAGE_BY_CONVERSATION_INDEX =
  'GeneratedImageByConversationIndex';

/**
 * Fixed sort key for every GeneratedImage metadata item.
 * Mirrors the pattern of ARTIFACT_HEAD_SK = "HEAD" in the Artifacts table.
 */
export const GENERATED_IMAGE_SK = 'META';

// ── Interfaces ────────────────────────────────────────────────────────────────

export interface AssistantImageAttachment {
  imageId: string;
  width: number;
  height: number;
}

/**
 * Tracks where this image has been attached / inserted.
 * Appended via DynamoDB `list_append` on each usage;
 * never queried in v1 (no reverse-lookup GSI needed for the hot path).
 */
export interface GeneratedImageUsage {
  /** Target entity type where the image was used. */
  target: GeneratedImageUsageTarget;
  /** Entity reference — e.g. commentId for DOBOX_COMMENT, pageId for BRAIN_PAGE. */
  ref: string;
  /** Unix epoch milliseconds when this image was attached/inserted. */
  attachedAt: number;
}

/**
 * Metadata item stored in DynamoDB.  Bytes are in S3 at `s3Bucket/s3Key`.
 *
 * To derive a public URL: generate a presigned GET URL for `s3Bucket/s3Key`
 * at read time (Phase 2 GraphQL feature).  Never store presigned URLs — they expire.
 *
 * Context fields (conversationId, runId, flowExecutionId) are mutually exclusive
 * in practice: an image is generated either by the agent chat surface (conversationId
 * + runId) or by a Flow execution (flowExecutionId), never both.
 */
export interface GeneratedImage {
  /** Primary key — unique image identifier (`img_<uuidv7>`). */
  imageId: string;
  /** Sort key — always GENERATED_IMAGE_SK ("META"). */
  sk: string;
  /** Clerk userId of the image owner. */
  ownerUserId: string;
  /**
   * The AgentConversation.conversationId this image was generated in.
   * Absent for images generated outside a conversation context (rare).
   */
  conversationId?: string;
  /**
   * The AgentRun.runId of the specific run that generated this image.
   * Enables per-run image lookup without a separate GSI.
   */
  runId?: string;
  /**
   * The flowExecutionId of the Flow run that produced this image (Phase 1b+).
   * Absent for images generated outside a flow (agent chat surface).
   * No GSI in v1 — persisted for future reverse-lookup queries.
   */
  flowExecutionId?: string;
  /** The text prompt used to generate the image. */
  prompt: string;
  /**
   * Optional negative prompt used to suppress unwanted content.
   * Both Stability text-to-image models (Core and Ultra) accept this field.
   */
  negativePrompt?: string;
  /** Quality tier used for generation. */
  quality: GeneratedImageQuality;
  /** Bedrock model ID that actually produced the image (may differ from requested model on fallback). */
  model: GeneratedImageModelId;
  /** Aspect ratio requested for generation. */
  aspectRatio: GeneratedImageAspectRatio;
  /** S3 bucket where the image bytes are stored. */
  s3Bucket: string;
  /** S3 object key within the bucket. */
  s3Key: string;
  /** Image width in pixels. */
  width: number;
  /** Image height in pixels. */
  height: number;
  /** Real MIME type of the stored bytes. New images default to "image/jpeg"; rows written before this change carry "image/png". */
  contentType: string;
  /** Unix epoch milliseconds when the image was created. */
  createdAt: number;
  /**
   * List of places where this image has been attached or inserted.
   * Appended to via DynamoDB `list_append` in Phase 5 (DoBox) and Phase 6 (Brain).
   * Absent until the image is first used.
   */
  usedIn?: GeneratedImageUsage[];
  /**
   * Origin of the bytes. Absent on rows written before this field existed —
   * every read treats absence as AI_GENERATED, so there is no backfill.
   */
  source?: GeneratedImageSource;
}
